import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { FieldProps, FormikProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import type { Dispatch, MouseEventHandler, SetStateAction } from 'react'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import type { SingleValue as SingleValueType } from 'react-select'
import Select from 'react-select'
import { vs2015 } from 'react-syntax-highlighter/dist/cjs/styles/hljs'
import styled from 'styled-components'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import ColorPicker from 'react-best-gradient-color-picker'
import moment from 'moment'
import { getFileURL } from '../../../utils/mediaLibrary'
import { useDebounce } from '../../hooks/useDebounce'
import useImageKitClient from '../../hooks/useImageKitClient'
import useToast from '../../hooks/useToast'
import type {
  FileDetailsResponse,
  MediaLibrarySearchFileResponse,
} from '../../interfaces'
import type { EmbedUrl } from '../../interfaces/MediaLibrarySearchResponse'
import { UserContext } from '../../providers'
import DateCustomInput from '../DateCustomInput'
import { ReactSyntaxHighlighter } from '../ReactSyntaxHighlighter'
import { colors } from '../../../ui-kit'
import useGetFileUrlArguments from '../../hooks/useGetFileUrlArguments'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'

interface Props {
  file: MediaLibrarySearchFileResponse | FileDetailsResponse
  openEmbedModal: boolean
  handleEmbedModal: Dispatch<SetStateAction<boolean>>
}

const codeBlockStyles = {
  backgroundColor: '#193055',
  padding: 30,
  lineHeight: '20px',
  fontSize: 14,
  marginRight: '40px',
  paddingTop: '3rem',
}

interface OptionType {
  value: string
  label: string
}

const options: OptionType[] = [
  { value: 'tr:sr-240_360_480_720_1080_2160', label: 'SD + HD + Full HD + 4K' },
  { value: 'tr:sr-240_360_480_720_1080', label: 'SD + HD + Full HD' },
  { value: 'tr:sr-720_1080', label: 'HD + Full HD' },
  { value: 'tr:sr-1440_2160', label: '4K' },
  { value: 'custom', label: 'Custom' },
]

const ColorPreview = React.forwardRef<
  HTMLDivElement,
  {
    color: string
    onClick: MouseEventHandler<HTMLDivElement> | undefined
  }
>(({ color, onClick }, ref) => (
  <Box
    width="2em"
    height="2em"
    borderRadius="var(--chakra-radii-md)"
    border="1px solid #ccc"
    background={color}
    onClick={onClick}
    ref={ref}
  />
))

const CopyEmbedURLModal = ({
  file,
  openEmbedModal,
  handleEmbedModal,
}: Props): React.ReactElement => {
  const { clientInfo } = useContext(PublicLinksInfoContext)
  const { t } = useTranslation()

  const formRef = useRef<FormikProps<EmbedUrl>>(null)

  const { user } = useContext(UserContext)
  const { updatedAt, mime } = file
  const { request } = useFetch<string>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getFileUrlDynamicArguments = useGetFileUrlArguments({
    clientInfo,
    user,
  })

  const { request: posterRequest } = useFetch<string>({
    cachePolicy: CachePolicies.NO_CACHE,
    headers: {
      'X-Get-Thumbnail-URL': 'video',
    },
  })

  const [transformationToCopy, setTransformationToCopy] = useState<Array<
    Record<string, string>
  > | null>(null)

  const [urlToCopy, setUrlToCopy] = useState<string>('')
  const [embedCode, setEmbedCode] = useState<string>('')
  const imagekit = useImageKitClient()
  const toast = useToast()
  const [errorDesc, setErrorDesc] = useState<string>('')
  const [embedOptions, setEmbedOptions] = useState<EmbedUrl>()
  const [regrenerateUrlRequired, setRegrenerateUrlRequired] =
    useState<boolean>(false)
  const debouncedFlag = useDebounce(regrenerateUrlRequired, 700)
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null)
  const [poster, setPoster] = useState<string>()
  const [showColorPicker, setShowColorPicker] = useState(false)
  const debouncedColorPreview = useDebounce(embedOptions?.background, 50)
  const colorPickerBoxRef = useRef<HTMLDivElement | null>(null)
  const colorPreviewRef = useRef<HTMLDivElement | null>(null)

  const handleSelectChange = (newValue: SingleValueType<OptionType>): void => {
    setSelectedOption(newValue)
    if (newValue?.value !== 'custom') {
      setEmbedOptions(prev => ({
        ...prev,
        transformationString: newValue?.value,
      }))
    } else {
      setEmbedOptions(prev => ({
        ...prev,
        transformationString: '',
      }))
    }
  }

  const ReactSyntaxHighlighterWrapper = styled(ReactSyntaxHighlighter)`
    .react-syntax-highlighter-line-number {
      color: #888 !important;
      padding-left: 0.5em !important;
      padding-right: 1.5em !important;
      min-width: 2.25em !important;
    }
    pre {
      margin: 0px !important;
    }
    code {
      span {
        font-family: monospace, monospace !important;
      }
    }
  `

  const updatedTransformationString: Record<string, string> | undefined =
    useMemo(() => {
      const tr_value = embedOptions?.transformationString?.split(':')[1]
      // eslint-disable-next-line no-undefined
      return tr_value ? { raw: tr_value } : undefined
    }, [embedOptions?.transformationString])

  const embedUrl = useMemo(() => {
    if (user?.imagekitId) {
      const baseUrl = `${user.dashboardServerURL}/player/embed/${user.imagekitId}${embedOptions?.videoPath}${embedOptions?.absSuffix}`
      const url = new URL(baseUrl)
      const controls = embedOptions?.controls ?? true
      const autoplay = embedOptions?.autoplay ?? false
      const loop = embedOptions?.loop ?? false
      const background = embedOptions?.background ?? '#000000'
      url.searchParams.append('controls', controls.toString())
      url.searchParams.append('autoplay', autoplay.toString())
      url.searchParams.append('loop', loop.toString())
      url.searchParams.append('background', background)
      return url.toString()
    }
    return ''
  }, [user?.dashboardServerURL, user?.imagekitId, embedOptions])

  const getAndSetPosterURL = async (): Promise<string> => {
    try {
      return getFileURL({
        file,
        request: posterRequest,
        ...getFileUrlDynamicArguments,
        urlFor: 'thumbnail',
      })
    } catch (err) {
      toast({
        description: t(
          'something_went_wrong',
          'Something went wrong. Please try again.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return ''
    }
  }

  useEffect(() => {
    if (file?.isPrivateFile && openEmbedModal) {
      void getAndSetPosterURL().then(posterUrl => {
        setPoster(posterUrl)
      })
    }
  }, [file, openEmbedModal])

  const generateUrl = useCallback(() => {
    if (transformationToCopy !== null) {
      let urlFor = ''
      if (formRef.current?.values.abs) {
        urlFor = `abs-${formRef.current.values.absType}`
      }
      void getFileURL({
        file,
        ...getFileUrlDynamicArguments,
        request,
        transformation: transformationToCopy,
        expirationTimestamp: embedOptions?.validUntil,
        urlFor,
      })
        .then(data => {
          function addDataUrlParamsToEmbedUrl(
            dataUrl: URL,
            embedBaseUrl: URL,
          ): void {
            dataUrl.searchParams.forEach((value, key) => {
              embedBaseUrl.searchParams.append(key, value)
            })
          }
          const dataUrl = new URL(data)
          const embedUrlParsed = new URL(embedUrl)
          if (data.includes('ik-s=')) {
            if (data.includes('ik-auth-version')) {
              dataUrl.searchParams.delete('ik-auth-version')
              dataUrl.searchParams.delete('ik-s')
              dataUrl.searchParams.delete('ik-t')
              setUrlToCopy(dataUrl.toString())
            } else if (openEmbedModal) {
              if (poster) {
                dataUrl.searchParams.append('thumbnail', poster)
              }
              addDataUrlParamsToEmbedUrl(dataUrl, embedUrlParsed)

              setUrlToCopy(embedUrlParsed.toString())
            } else {
              setUrlToCopy(data)
            }
            setRegrenerateUrlRequired(false)
          } else {
            if (file.thumbnail) {
              dataUrl.searchParams.append('thumbnail', file.thumbnail)
            }
            addDataUrlParamsToEmbedUrl(dataUrl, embedUrlParsed)
            setUrlToCopy(
              imagekit.url({
                src: embedUrlParsed.toString(),
                transformation: transformationToCopy,
              }),
            )
          }
        })
        .catch(() => {
          toast({
            description: t(
              'generating_url_to_copy_error',
              'There was an error in generating the URL',
            ),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        })
    }
  }, [
    getFileUrlDynamicArguments,
    transformationToCopy,
    file,
    user,
    request,
    setUrlToCopy,
    embedUrl,
    embedOptions?.validUntil,
    poster,
    setRegrenerateUrlRequired,
  ])

  useEffect(() => {
    if (updatedTransformationString) {
      setTransformationToCopy([updatedTransformationString])
    } else {
      setTransformationToCopy([])
    }
  }, [updatedTransformationString])

  useEffect(() => {
    if (openEmbedModal) {
      setEmbedOptions({
        videoPath: new URL(file.url).pathname.replace(
          `/${user?.imagekitId}`,
          '',
        ),
        transformationString: '',
        absSuffix: '',
        absTr: '',
        absType: '',
        autoplay: false,
        loop: false,
        controls: true,
        background: '#000000',
      })
      setTransformationToCopy(
        updatedTransformationString ? [updatedTransformationString] : [],
      )
      setRegrenerateUrlRequired(false)
    }
  }, [file, openEmbedModal, setTransformationToCopy, setRegrenerateUrlRequired])

  useEffect(() => {
    setEmbedCode(
      `<iframe width="560" height="315" src="${urlToCopy}" title="ImageKit video player" frameBorder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"> </iframe>`,
    )
  }, [urlToCopy, setEmbedCode])

  useEffect(() => {
    if ((!file.isPrivateFile || !urlToCopy) && openEmbedModal) {
      generateUrl()
    } else {
      setRegrenerateUrlRequired(true)
    }
  }, [transformationToCopy, embedUrl, generateUrl, openEmbedModal])

  useEffect(() => {
    if (debouncedFlag && openEmbedModal) {
      generateUrl()
    }
  }, [debouncedFlag, generateUrl, openEmbedModal])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const colorPickerBox = colorPickerBoxRef.current
      const colorPreview = colorPreviewRef.current
      if (
        colorPickerBox &&
        !colorPickerBox.contains(event.target as Node) &&
        !colorPreview?.contains(event.target as Node)
      ) {
        setShowColorPicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <Modal
      isOpen={openEmbedModal}
      onClose={() => {
        handleEmbedModal(false)
        setErrorDesc('')
        setUrlToCopy('')
      }}
      size="2xl"
    >
      <ModalOverlay />
      <ModalContent
        onClick={e => void e.stopPropagation()}
        onDoubleClick={e => void e.stopPropagation()}
        onContextMenu={e => void e.stopPropagation()}
      >
        <ModalHeader>{t('embed_video', 'Embed Video')}</ModalHeader>
        <ModalCloseButton />
        <Formik<EmbedUrl>
          innerRef={formRef}
          onSubmit={() => {
            navigator.clipboard
              .writeText(embedCode)
              .then(() => {
                toast({
                  description: t(
                    'copied_embed_code_to_clipboard',
                    'Copied embed code to clipboard',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              })
              .catch(() => {
                toast({
                  description: t(
                    'copy_embed_code_failed',
                    'Failed to copy embed code to clipboard',
                  ),
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              })
            handleEmbedModal(false)
            setErrorDesc('')
          }}
          initialValues={{ ...embedOptions, neverExpire: true }}
          validationSchema={Yup.object().shape({
            videoPath: Yup.string().required(),
            transformationString: Yup.string().matches(
              /^tr:/gu,
              "Transformations must begin with 'tr:'",
            ),
          })}
        >
          {({ setFieldValue, errors, touched, handleChange, values }) => (
            <Form>
              <ModalBody>
                <Box position="relative">
                  <Flex
                    position="absolute"
                    top="1rem"
                    right="1rem"
                    cursor="pointer"
                    justifyContent="end"
                  >
                    <Button
                      colorScheme="appClearBlue"
                      type="submit"
                      disabled={regrenerateUrlRequired}
                    >
                      {t('copy')}
                    </Button>
                  </Flex>
                  <Box opacity={regrenerateUrlRequired ? 0.8 : 1}>
                    <ReactSyntaxHighlighterWrapper
                      language="jsx"
                      style={vs2015}
                      wrapLines={true}
                      showLineNumbers={false}
                      customStyle={codeBlockStyles}
                      codeTagProps={{
                        style: {
                          fontFamily: 'monospace,monospace !important',
                          paddingTop: '2.5rem',
                        },
                      }}
                    >
                      {embedCode}
                    </ReactSyntaxHighlighterWrapper>
                  </Box>
                  {regrenerateUrlRequired && (
                    <Flex
                      position="absolute"
                      top="50%"
                      right="50%"
                      cursor="pointer"
                      justifyContent="end"
                    >
                      <Spinner
                        size="lg"
                        emptyColor={colors.appWhite}
                        color={colors.appGreyFour}
                      />
                    </Flex>
                  )}
                </Box>
                <Accordion width="full" allowToggle mt="1rem" border={0}>
                  <AccordionItem width="full" border={0}>
                    <AccordionButton>
                      <Flex flex="1" textAlign="left" fontWeight="bold">
                        <Text fontWeight={600}>Embed options</Text>
                      </Flex>
                      <Box
                        background="appPaleGreyThree"
                        borderRadius="full"
                        padding={1}
                      >
                        <AccordionIcon color="appSlate" display="block" />
                      </Box>
                    </AccordionButton>
                    <AccordionPanel>
                      <VStack spacing="1em">
                        {errorDesc ? (
                          <Alert status="error">
                            <AlertIcon />
                            <AlertDescription>{errorDesc}</AlertDescription>
                          </Alert>
                        ) : null}
                        <Box w="100%">
                          {file.isPrivateFile && (
                            <VStack
                              alignItems="flex-end"
                              mt="1em"
                              mb="2em"
                              spacing="1em"
                            >
                              <Alert status="warning">
                                <AlertDescription>
                                  {t(
                                    'embed_private_info',
                                    'This video has been marked as private. If you intend to embed this video, it is strongly recommended to set an expiry time.',
                                  )}
                                </AlertDescription>
                              </Alert>
                              <Field name="neverExpire">
                                {({
                                  field,
                                }: FieldProps<
                                  boolean | undefined,
                                  EmbedUrl
                                >) => (
                                  <FormControl mb="0.5em">
                                    <Checkbox
                                      isChecked={Boolean(field.value)}
                                      defaultChecked={true}
                                      onChange={event => {
                                        if (event.target.checked) {
                                          handleChange({
                                            target: {
                                              name: 'validUntil',
                                              value: undefined,
                                            },
                                          })
                                          setEmbedOptions(prev => ({
                                            ...prev,
                                            validUntil: undefined,
                                          }))
                                        }
                                        handleChange({
                                          target: {
                                            name: 'neverExpire',
                                            value: event.target.checked,
                                          },
                                        })
                                      }}
                                    >
                                      {t('neverExpire', 'Never expire')}
                                    </Checkbox>
                                  </FormControl>
                                )}
                              </Field>
                              {!values.neverExpire && (
                                <Field name="validUntil">
                                  {({
                                    field,
                                  }: FieldProps<
                                    string | number | undefined,
                                    EmbedUrl
                                  >) => (
                                    <FormControl
                                      isInvalid={Boolean(
                                        errors.validUntil && touched.validUntil,
                                      )}
                                      isRequired
                                    >
                                      <FormLabel>
                                        {t('validUntil', 'Valid until')}
                                      </FormLabel>
                                      <DatePicker
                                        selected={
                                          field.value
                                            ? new Date(field.value)
                                            : undefined
                                        }
                                        isClearable
                                        showTimeSelect
                                        minDate={new Date()}
                                        minTime={
                                          field.value &&
                                          new Date(field.value) > new Date()
                                            ? new Date(moment().startOf('day').unix() * 1000)
                                            : new Date()
                                        }
                                        maxTime={
                                          new Date(
                                            moment().endOf('day').unix() * 1000,
                                          )
                                        }
                                        customInput={React.createElement(
                                          React.forwardRef(DateCustomInput),
                                        )}
                                        dateFormat="MM/dd/yyyy h:mm aa z"
                                        placeholderText="Pick a date and time"
                                        onChange={(newDate: Date | null) => {
                                          handleChange({
                                            target: {
                                              name: 'validUntil',
                                              value: newDate?.getTime(),
                                            },
                                          })
                                          setEmbedOptions(prev => ({
                                            ...prev,
                                            validUntil: newDate?.getTime(),
                                          }))
                                        }}
                                      />
                                    </FormControl>
                                  )}
                                </Field>
                              )}
                            </VStack>
                          )}
                          <Field name="abs">
                            {({
                              field,
                            }: FieldProps<boolean | undefined, EmbedUrl>) => (
                              <FormControl
                                marginTop="1em"
                                display="flex"
                                alignItems="center"
                                style={{ gap: '1em' }}
                              >
                                <Switch
                                  name="abs"
                                  id="abs"
                                  isChecked={field.value}
                                  onChange={e => {
                                    setFieldValue('abs', e.target.checked)
                                    setFieldValue('absType', 'hls')
                                    let tempTr: string | undefined
                                    let tempAbsSuffix: string | undefined
                                    if (e.target.checked) {
                                      tempTr = 'tr:sr-240_360_480_720_1080'
                                      tempAbsSuffix = '/ik-master.m3u8'
                                      handleSelectChange({
                                        value: 'tr:sr-240_360_480_720_1080',
                                        label: 'SD + HD + Full HD',
                                      })
                                    } else if (!e.target.checked) {
                                      tempTr = ''
                                      tempAbsSuffix = ''
                                    }
                                    setFieldValue(
                                      'transformationString',
                                      tempTr,
                                    )
                                    setEmbedOptions(prev => ({
                                      ...prev,
                                      absSuffix: tempAbsSuffix,
                                      transformationString: tempTr,
                                    }))
                                  }}
                                />
                                <FormLabel margin={0}>
                                  {t('enabled_abs', 'Enable ABS')}
                                </FormLabel>
                              </FormControl>
                            )}
                          </Field>
                          {values.abs && (
                            <Field name="absType">
                              {({ field }: FieldProps<string, EmbedUrl>) => (
                                <RadioGroup {...field} w="100%" mt="1em">
                                  <VStack
                                    spacing="1em"
                                    direction="column"
                                    alignItems="flex-start"
                                  >
                                    <Flex
                                      w="100%"
                                      gap="10px"
                                      justifyContent="space-between"
                                    >
                                      <VStack
                                        w="47%"
                                        spacing="0.5em"
                                        alignItems="flex-start"
                                      >
                                        <Radio
                                          value="hls"
                                          isChecked={values.absType === 'hls'}
                                          onChange={value => {
                                            handleChange(value)
                                            setEmbedOptions(prev => ({
                                              ...prev,
                                              absSuffix: '/ik-master.m3u8',
                                              transformationString:
                                                'tr:sr-240_360_480_720_1080',
                                            }))
                                            setFieldValue(
                                              'transformationString',
                                              'tr:sr-240_360_480_720_1080',
                                            )
                                            handleSelectChange({
                                              value:
                                                'tr:sr-240_360_480_720_1080',
                                              label: 'SD + HD + Full HD',
                                            })
                                          }}
                                        >
                                          {t('hls', 'HLS')}
                                        </Radio>
                                        {values.absType === 'hls' && (
                                          <Box w="100%">
                                            <Select
                                              options={options}
                                              value={selectedOption}
                                              onChange={newValue => {
                                                handleSelectChange(newValue)
                                                setFieldValue(
                                                  'transformationString',
                                                  newValue?.value === 'custom'
                                                    ? ''
                                                    : newValue?.value,
                                                )
                                              }}
                                            />
                                          </Box>
                                        )}
                                      </VStack>
                                      {selectedOption?.value === 'custom' &&
                                        values.absType === 'hls' && (
                                          <Box w="47%">
                                            <Field name="transformationString">
                                              {({
                                                field,
                                              }: FieldProps<
                                                string,
                                                EmbedUrl
                                              >) => (
                                                <FormControl
                                                  isInvalid={Boolean(
                                                    errors.transformationString &&
                                                      touched.transformationString,
                                                  )}
                                                >
                                                  <FormLabel
                                                    mb="0.5em"
                                                    fontWeight={400}
                                                  >
                                                    {t(
                                                      'enter_transformation_string',
                                                      'Enter transformation string',
                                                    )}
                                                  </FormLabel>
                                                  <Input
                                                    {...field}
                                                    placeholder="tr:sr-240_480"
                                                    onChange={event => {
                                                      handleChange(event)
                                                      setEmbedOptions(prev => ({
                                                        ...prev,
                                                        transformationString:
                                                          event.target.value,
                                                      }))
                                                    }}
                                                    h="2.8em"
                                                  />
                                                  <FormErrorMessage marginBottom="-2.0408em">
                                                    {
                                                      errors.transformationString
                                                    }
                                                  </FormErrorMessage>
                                                </FormControl>
                                              )}
                                            </Field>
                                          </Box>
                                        )}
                                    </Flex>

                                    <Flex
                                      w="100%"
                                      gap="10px"
                                      justifyContent="space-between"
                                    >
                                      {' '}
                                      <VStack
                                        w="47%"
                                        spacing="0.5em"
                                        alignItems="flex-start"
                                      >
                                        <Radio
                                          value="dash"
                                          isChecked={values.absType === 'dash'}
                                          onChange={value => {
                                            handleChange(value)
                                            setEmbedOptions(prev => ({
                                              ...prev,
                                              absSuffix: '/ik-master.mpd',
                                              transformationString:
                                                'tr:sr-240_360_480_720_1080',
                                            }))
                                            setFieldValue(
                                              'transformationString',
                                              'tr:sr-240_360_480_720_1080',
                                            )
                                            handleSelectChange({
                                              value:
                                                'tr:sr-240_360_480_720_1080',
                                              label: 'SD + HD + Full HD',
                                            })
                                          }}
                                        >
                                          {t('dash', 'DASH')}
                                        </Radio>
                                        {values.absType === 'dash' && (
                                          <Box w="100%">
                                            <Select
                                              options={options}
                                              value={selectedOption}
                                              onChange={newValue => {
                                                handleSelectChange(newValue)
                                                setFieldValue(
                                                  'transformationString',
                                                  newValue?.value === 'custom'
                                                    ? ''
                                                    : newValue?.value,
                                                )
                                              }}
                                            />
                                          </Box>
                                        )}
                                      </VStack>
                                      {selectedOption?.value === 'custom' &&
                                        values.absType === 'dash' && (
                                          <Box w="47%">
                                            <Field name="transformationString">
                                              {({
                                                field,
                                              }: FieldProps<
                                                string,
                                                EmbedUrl
                                              >) => (
                                                <FormControl
                                                  isInvalid={Boolean(
                                                    errors.transformationString &&
                                                      touched.transformationString,
                                                  )}
                                                >
                                                  <FormLabel
                                                    mb="0.5em"
                                                    fontWeight={400}
                                                  >
                                                    {t(
                                                      'enter_transformation_string',
                                                      'Enter transformation string',
                                                    )}
                                                  </FormLabel>
                                                  <Input
                                                    {...field}
                                                    placeholder="tr:sr-240_480"
                                                    onChange={event => {
                                                      handleChange(event)
                                                      setEmbedOptions(prev => ({
                                                        ...prev,
                                                        transformationString:
                                                          event.target.value,
                                                      }))
                                                    }}
                                                    h="2.8em"
                                                  />
                                                  <FormErrorMessage marginBottom="-2.0408em">
                                                    {
                                                      errors.transformationString
                                                    }
                                                  </FormErrorMessage>
                                                </FormControl>
                                              )}
                                            </Field>
                                          </Box>
                                        )}
                                    </Flex>
                                  </VStack>
                                </RadioGroup>
                              )}
                            </Field>
                          )}
                        </Box>
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
                <Accordion
                  width="full"
                  allowToggle
                  mt="1rem"
                  border={0}
                  onChange={() => void setShowColorPicker(false)}
                >
                  <AccordionItem width="full" border={0}>
                    <AccordionButton>
                      <Flex flex="1" textAlign="left" fontWeight="bold">
                        <Text fontWeight={600}>Video player options</Text>
                      </Flex>
                      <Box
                        background="appPaleGreyThree"
                        borderRadius="full"
                        padding={1}
                      >
                        <AccordionIcon color="appSlate" display="block" />
                      </Box>
                    </AccordionButton>
                    <AccordionPanel>
                      <VStack spacing="1em">
                        {errorDesc ? (
                          <Alert status="error">
                            <AlertIcon />
                            <AlertDescription>{errorDesc}</AlertDescription>
                          </Alert>
                        ) : null}
                        <Box w="100%">
                          <Field name="controls">
                            {({
                              field,
                            }: FieldProps<boolean | undefined, EmbedUrl>) => (
                              <FormControl
                                marginTop="1em"
                                display="flex"
                                alignItems="center"
                                style={{ gap: '1em' }}
                              >
                                <Switch
                                  name="controls"
                                  id="controls"
                                  isChecked={field.value}
                                  onChange={e => {
                                    setFieldValue('controls', e.target.checked)
                                    setEmbedOptions(prev => ({
                                      ...prev,
                                      controls: e.target.checked,
                                    }))
                                  }}
                                />
                                <FormLabel margin={0}>
                                  {t('enable_controls', 'Show controls')}
                                </FormLabel>
                              </FormControl>
                            )}
                          </Field>
                          <Field name="autoplay">
                            {({
                              field,
                            }: FieldProps<boolean | undefined, EmbedUrl>) => (
                              <FormControl
                                marginTop="1em"
                                display="flex"
                                alignItems="center"
                                style={{ gap: '1em' }}
                              >
                                <Switch
                                  name="autoplay"
                                  id="autoplay"
                                  isChecked={field.value}
                                  onChange={e => {
                                    setFieldValue('autoplay', e.target.checked)
                                    setEmbedOptions(prev => ({
                                      ...prev,
                                      autoplay: e.target.checked,
                                    }))
                                  }}
                                />
                                <FormLabel margin={0}>
                                  {t('enable_autoplay', 'Autoplay')}
                                </FormLabel>
                              </FormControl>
                            )}
                          </Field>
                          <Field name="loop">
                            {({
                              field,
                            }: FieldProps<boolean | undefined, EmbedUrl>) => (
                              <FormControl
                                marginTop="1em"
                                display="flex"
                                alignItems="center"
                                style={{ gap: '1em' }}
                              >
                                <Switch
                                  name="loop"
                                  id="loop"
                                  isChecked={field.value}
                                  onChange={e => {
                                    setFieldValue('loop', e.target.checked)
                                    setEmbedOptions(prev => ({
                                      ...prev,
                                      loop: e.target.checked,
                                    }))
                                  }}
                                />
                                <FormLabel margin={0}>
                                  {t('enable_looping', 'Looping')}
                                </FormLabel>
                              </FormControl>
                            )}
                          </Field>
                          <Field name="background">
                            {({
                              field,
                            }: FieldProps<string | undefined, EmbedUrl>) => (
                              <FormControl
                                marginTop="1em"
                                display="flex"
                                style={{ gap: '1em' }}
                                flexDirection={'column'}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  style={{ gap: '1em' }}
                                >
                                  <ColorPreview
                                    color={debouncedColorPreview ?? '#000000'}
                                    ref={colorPreviewRef}
                                    onClick={() => {
                                      setShowColorPicker(prev => !prev)
                                    }}
                                  />
                                  <FormLabel margin={0} marginLeft="0.27em">
                                    {t('video_background', 'Background')}
                                  </FormLabel>
                                </Box>
                                <Box ref={colorPickerBoxRef}>
                                  <ColorPicker
                                    value={field.value ?? '#000000'}
                                    onChange={colorValue => {
                                      setFieldValue('background', colorValue)
                                      setEmbedOptions(prev => ({
                                        ...prev,
                                        background: colorValue,
                                      }))
                                    }}
                                    style={{
                                      body: {
                                        padding: '0.5em',
                                        border: 'none',
                                        position: 'absolute',
                                        visibility: showColorPicker
                                          ? 'visible'
                                          : 'hidden',
                                        opacity: showColorPicker ? 1 : 0,
                                        transition:
                                          'visibility 0.1s, opacity 0.1s linear',
                                        zIndex: 10,
                                        left: '15em',
                                        top: '-30.5em',
                                        boxShadow:
                                          '0 0 2px 2px rgba(0, 0, 0, .2)',
                                        borderRadius: 'var(--chakra-radii-md)',
                                        boxSizing: 'content-box',
                                      },
                                    }}
                                    disableDarkMode
                                    hidePresets
                                  />
                                </Box>
                              </FormControl>
                            )}
                          </Field>
                        </Box>
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </ModalBody>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default CopyEmbedURLModal
